import { useEffect } from 'react';

import type { RefObject } from 'react';

type UseScrollShadowsOptions = {
  enabled?: boolean;
  fadeDistance?: number;
  maxOpacity?: number;
  scrollRef: RefObject<HTMLElement | null>;
  viewportRef: RefObject<HTMLElement | null>;
};

const DEFAULT_FADE_DISTANCE = 64;
const DEFAULT_MAX_OPACITY = 0.15;

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

const toFixedOpacity = (value: number): string => clamp(value, 0, 1).toFixed(3);

const setOpacityVariables = (
  viewport: HTMLElement,
  topOpacity: number,
  rightOpacity: number,
  bottomOpacity: number,
  leftOpacity: number,
): void => {
  viewport.style.setProperty('--layout-scroll-shadow-top-opacity', toFixedOpacity(topOpacity));
  viewport.style.setProperty('--layout-scroll-shadow-right-opacity', toFixedOpacity(rightOpacity));
  viewport.style.setProperty('--layout-scroll-shadow-bottom-opacity', toFixedOpacity(bottomOpacity));
  viewport.style.setProperty('--layout-scroll-shadow-left-opacity', toFixedOpacity(leftOpacity));
};

const setScrollbarInsetVariables = (
  viewport: HTMLElement,
  scrollbarInlineSize: number,
  scrollbarBlockSize: number,
): void => {
  viewport.style.setProperty('--layout-scroll-shadow-scrollbar-inline-size', `${Math.max(0, scrollbarInlineSize)}px`);
  viewport.style.setProperty('--layout-scroll-shadow-scrollbar-block-size', `${Math.max(0, scrollbarBlockSize)}px`);
};

const setAxisModeVariables = (
  viewport: HTMLElement,
  hasVerticalOverflow: boolean,
  hasHorizontalOverflow: boolean,
): void => {
  const isMultiAxisScrollable = hasVerticalOverflow && hasHorizontalOverflow;

  viewport.style.setProperty('--layout-scroll-shadow-multi-axis-factor', isMultiAxisScrollable ? '1' : '0');
};

const resetOpacityVariables = (viewport: HTMLElement): void => {
  setOpacityVariables(viewport, 0, 0, 0, 0);
  setScrollbarInsetVariables(viewport, 0, 0);
  setAxisModeVariables(viewport, false, false);
};

export const useScrollShadows = ({
  enabled = true,
  fadeDistance = DEFAULT_FADE_DISTANCE,
  maxOpacity = DEFAULT_MAX_OPACITY,
  scrollRef,
  viewportRef,
}: UseScrollShadowsOptions): void => {
  useEffect(() => {
    const viewport = viewportRef.current;

    if (!viewport) {
      return;
    }

    if (!enabled) {
      resetOpacityVariables(viewport);

      return;
    }

    const scrollElement = scrollRef.current;

    if (!scrollElement) {
      resetOpacityVariables(viewport);

      return;
    }

    let raf = 0;

    const scheduleUpdate = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const maxScrollTop = Math.max(0, scrollElement.scrollHeight - scrollElement.clientHeight);
        const maxScrollLeft = Math.max(0, scrollElement.scrollWidth - scrollElement.clientWidth);
        const hasVerticalOverflow = maxScrollTop > 0;
        const hasHorizontalOverflow = maxScrollLeft > 0;
        const scrollbarInlineSize = scrollElement.offsetWidth - scrollElement.clientWidth;
        const scrollbarBlockSize = scrollElement.offsetHeight - scrollElement.clientHeight;
        const clampedTop = clamp(scrollElement.scrollTop, 0, maxScrollTop);
        const clampedLeft = clamp(scrollElement.scrollLeft, 0, maxScrollLeft);

        const topStrength = maxScrollTop > 0 ? Math.min(clampedTop / fadeDistance, 1) : 0;
        const bottomStrength = maxScrollTop > 0 ? Math.min((maxScrollTop - clampedTop) / fadeDistance, 1) : 0;
        const leftStrength = maxScrollLeft > 0 ? Math.min(clampedLeft / fadeDistance, 1) : 0;
        const rightStrength = maxScrollLeft > 0 ? Math.min((maxScrollLeft - clampedLeft) / fadeDistance, 1) : 0;

        setOpacityVariables(
          viewport,
          topStrength * maxOpacity,
          rightStrength * maxOpacity,
          bottomStrength * maxOpacity,
          leftStrength * maxOpacity,
        );
        setScrollbarInsetVariables(viewport, scrollbarInlineSize, scrollbarBlockSize);
        setAxisModeVariables(viewport, hasVerticalOverflow, hasHorizontalOverflow);
      });
    };

    const resizeObserver = new ResizeObserver(scheduleUpdate);

    resizeObserver.observe(scrollElement);

    if (scrollElement.firstElementChild instanceof HTMLElement) {
      resizeObserver.observe(scrollElement.firstElementChild);
    }

    scrollElement.addEventListener('scroll', scheduleUpdate, { passive: true });
    globalThis.addEventListener('resize', scheduleUpdate);
    scheduleUpdate();

    return () => {
      scrollElement.removeEventListener('scroll', scheduleUpdate);
      globalThis.removeEventListener('resize', scheduleUpdate);
      resizeObserver.disconnect();
      cancelAnimationFrame(raf);
      resetOpacityVariables(viewport);
    };
  }, [enabled, fadeDistance, maxOpacity, scrollRef, viewportRef]);
};
